import React, { useState, useEffect, useRef, useCallback } from 'react';
import { apiFetch } from '../api/apiFetch';
import {
    Box, Drawer, Typography, Button, IconButton, Divider,
    Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Chip, Tooltip, CircularProgress,
} from '@mui/material';
import {
    ChevronLeft as ChevronLeftIcon,
    Send as SendIcon,
    Clear as ClearIcon,
    ArrowBack as ArrowBackIcon,
    Science as ScienceIcon,
    TableChart as TableChartIcon,
    CheckCircle as CheckCircleIcon,
    Warning as WarningIcon,
    Error as ErrorIcon,
} from '@mui/icons-material';
import NavigationBar from '../components/NavigationBar';
import MSAStudyTypeSelector from '../components/MSAStudyTypeSelector';
import GRRDataEntryTable from '../components/GRRDataEntryTable';
import GRRResultsSection from '../components/GRRResultsSection';
import GRRChartSection from '../components/GRRChartSection';
import Type1GRRPage from './Type1GRRPage';
import CapabilityImpactModule from '../components/msaModules/CapabilityImpactModule';
import AttributeAgreementModule from '../components/msaModules/AttributeAgreementModule';
import AttributeAnalyticModule from '../components/msaModules/AttributeAnalyticModule';
import Type3GRRModule from '../components/msaModules/Type3GRRModule';

const DRAWER_WIDTH = 340;

// MSA-1/3/4/5 module pages — each fills the flex row with its own MSAModulePage chrome.
const MODULE_COMPONENTS = {
    capabilityImpact:  CapabilityImpactModule,
    attribute:         AttributeAgreementModule,
    attributeAnalytic: AttributeAnalyticModule,
    type3:             Type3GRRModule,
};

// ---------------------------------------------------------------------------
// Acceptance styling helpers (mirrors GRRResultsSection)
// ---------------------------------------------------------------------------

function acceptanceColor(status) {
    return { acceptable: '#81c784', conditional: '#ffb74d', unacceptable: '#e57373' }[status] || '#90caf9';
}

function AcceptanceChip({ status }) {
    const color = acceptanceColor(status);
    const Icon  = { acceptable: CheckCircleIcon, conditional: WarningIcon, unacceptable: ErrorIcon }[status];
    const label = { acceptable: 'Acceptable', conditional: 'Conditional', unacceptable: 'Unacceptable' }[status] || status || '—';

    if (!status) return <Chip label="—" size="small" sx={{ color: 'rgba(255,255,255,0.4)' }} />;

    return (
        <Chip
            icon={Icon ? <Icon sx={{ fontSize: '0.75rem !important', color: `${color} !important` }} /> : undefined}
            label={label}
            size="small"
            sx={{
                backgroundColor: `${color}18`,
                border: `1px solid ${color}55`,
                color,
                fontWeight: 600,
                fontSize: '0.72rem',
            }}
        />
    );
}

// ---------------------------------------------------------------------------
// Previous studies table
// ---------------------------------------------------------------------------

function StudyHistoryTable({ studies, onSelect }) {
    if (!studies.length) {
        return (
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.35)', p: 2, textAlign: 'center' }}>
                No previous studies found. Submit a study to see results here.
            </Typography>
        );
    }

    return (
        <TableContainer>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        {['Date', 'Description', 'Design', '%GRR', 'NDC', 'Status'].map(h => (
                            <TableCell
                                key={h}
                                sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid rgba(255,255,255,0.1)', py: 0.75, px: 1.5 }}
                            >
                                {h}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {studies.map((study) => (
                        <TableRow
                            key={study.id}
                            onClick={() => onSelect && onSelect(study)}
                            sx={{
                                cursor: onSelect ? 'pointer' : 'default',
                                '&:hover': { backgroundColor: 'rgba(144,202,249,0.05)' },
                                borderBottom: '1px solid rgba(255,255,255,0.05)',
                            }}
                        >
                            <TableCell sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.75rem', py: 0.75, px: 1.5, whiteSpace: 'nowrap' }}>
                                {new Date(study.created_at).toLocaleDateString()}
                            </TableCell>
                            <TableCell sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.8rem', py: 0.75, px: 1.5, maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                <Tooltip title={study.description} placement="top-start" arrow>
                                    <span>{study.description}</span>
                                </Tooltip>
                            </TableCell>
                            <TableCell sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.75rem', py: 0.75, px: 1.5, whiteSpace: 'nowrap' }}>
                                {study.num_parts && study.num_operators && study.num_trials
                                    ? `${study.num_parts}×${study.num_operators}×${study.num_trials}`
                                    : '—'}
                            </TableCell>
                            <TableCell sx={{ py: 0.75, px: 1.5 }}>
                                <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: study.percent_grr !== null ? acceptanceColor(study.acceptance_status) : 'rgba(255,255,255,0.4)' }}>
                                    {study.percent_grr !== null ? `${Number(study.percent_grr).toFixed(1)}%` : '—'}
                                </Typography>
                            </TableCell>
                            <TableCell sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.8rem', py: 0.75, px: 1.5 }}>
                                {study.ndc ?? '—'}
                            </TableCell>
                            <TableCell sx={{ py: 0.75, px: 1.5 }}>
                                <AcceptanceChip status={study.acceptance_status} />
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

function MSAStudyPage() {
    const dataEntryRef = useRef(null);

    // Study type selection
    const [studyType, setStudyType]       = useState(null);    // null = selector shown
    const [drawerOpen, setDrawerOpen]     = useState(true);

    // Results from last submission
    const [studyResults, setStudyResults] = useState(null);    // { results, measurements, description, id, createdAt }
    const [submitting, setSubmitting]     = useState(false);

    // Study history
    const [history, setHistory]           = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);

    // ---------------------------------------------------------------------------
    // Fetch history
    // ---------------------------------------------------------------------------
    const fetchHistory = useCallback(async () => {
        setHistoryLoading(true);
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
            const res = await apiFetch(`${backendUrl}/api/msa-studies`, { credentials: 'include' });
            if (res.ok) {
                const data = await res.json();
                setHistory(data.studies || []);
            }
        } catch (e) {
            console.error('Failed to fetch MSA history:', e);
        } finally {
            setHistoryLoading(false);
        }
    }, []);

    useEffect(() => { fetchHistory(); }, [fetchHistory]);

    // ---------------------------------------------------------------------------
    // Event handlers
    // ---------------------------------------------------------------------------

    const handleStudyComplete = useCallback((apiResponse) => {
        // apiResponse: { id, createdAt, studyType, description, results, warnings }
        const measurements = dataEntryRef.current?.getLastMeasurements?.() || [];

        setStudyResults({
            id:           apiResponse.id,
            createdAt:    apiResponse.createdAt,
            description:  apiResponse.description,
            results:      apiResponse.results,
            measurements,
        });
        setSubmitting(false);
        fetchHistory();
    }, [fetchHistory]);

    const handleSubmit = () => {
        if (dataEntryRef.current) {
            setSubmitting(true);
            dataEntryRef.current.submit();
        }
    };

    const handleClear = () => {
        setStudyResults(null);
    };

    const handleSelectStudyType = (type) => {
        setStudyType(type);
        setStudyResults(null);
        setDrawerOpen(true);
    };

    const handleBack = () => {
        setStudyType(null);
        setStudyResults(null);
    };

    // ---------------------------------------------------------------------------
    // Derived state
    // ---------------------------------------------------------------------------

    const isType1         = studyType === 'type1';
    const isType2         = studyType === 'type2';
    const ModuleComponent = studyType ? MODULE_COMPONENTS[studyType] : null;
    const isModule        = Boolean(ModuleComponent);
    const hasResults      = Boolean(studyResults?.results);

    // ---------------------------------------------------------------------------
    // Render
    // ---------------------------------------------------------------------------

    return (
        <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: '#121212' }}>
            <NavigationBar />

            <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

                {/* ---- Type 1 GRR — fills the full flex row with its own drawer ---- */}
                {isType1 && <Type1GRRPage onBack={handleBack} />}

                {/* ---- MSA-1/3/4/5 module pages — each fills the full flex row ---- */}
                {isModule && <ModuleComponent onBack={handleBack} />}

                {/* ---- Type 2 + selector + previous studies ---- */}
                {!isType1 && !isModule && (
                    <>
                        {/* ---------------------------------------------------------- */}
                        {/* Left Drawer — Type 2 only                                  */}
                        {/* ---------------------------------------------------------- */}
                        {isType2 && (
                            <Drawer
                                variant="persistent"
                                anchor="left"
                                open={drawerOpen}
                                sx={{
                                    width: drawerOpen ? DRAWER_WIDTH : 0,
                                    flexShrink: 0,
                                    '& .MuiDrawer-paper': {
                                        width: DRAWER_WIDTH,
                                        boxSizing: 'border-box',
                                        backgroundColor: 'rgba(30, 36, 44, 0.98)',
                                        borderRight: '1px solid rgba(144, 202, 249, 0.15)',
                                        position: 'relative',
                                        height: '100%',
                                    },
                                }}
                            >
                                <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', p: 1.5 }}>
                                    {/* Drawer header */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1, pb: 1, borderBottom: '1px solid rgba(144,202,249,0.12)', flexShrink: 0 }}>
                                        <IconButton
                                            size="small"
                                            onClick={handleBack}
                                            sx={{ color: 'rgba(255,255,255,0.5)', p: 0.5, mr: 0.5, '&:hover': { color: '#90caf9' } }}
                                            title="Back to study selector"
                                        >
                                            <ArrowBackIcon fontSize="small" />
                                        </IconButton>
                                        <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 600, mr: 'auto', fontSize: '0.8rem' }}>
                                            Type 2 Crossed GRR
                                        </Typography>
                                        <IconButton
                                            size="small"
                                            onClick={handleClear}
                                            disabled={!hasResults}
                                            sx={{ color: hasResults ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.15)', p: 0.5, '&:hover': { color: '#ff8a80' } }}
                                            title="Clear results"
                                        >
                                            <ClearIcon fontSize="small" />
                                        </IconButton>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            size="small"
                                            onClick={handleSubmit}
                                            disabled={submitting}
                                            startIcon={submitting ? <CircularProgress size={12} color="inherit" /> : <SendIcon />}
                                            sx={{ py: 0.25, px: 1.5, fontSize: '0.75rem', ml: 0.5 }}
                                        >
                                            {submitting ? 'Running…' : 'Run Study'}
                                        </Button>
                                        <IconButton
                                            onClick={() => setDrawerOpen(false)}
                                            size="small"
                                            sx={{ color: 'rgba(144,202,249,0.6)', p: 0.5, ml: 0.25 }}
                                            title="Collapse panel"
                                        >
                                            <ChevronLeftIcon fontSize="small" />
                                        </IconButton>
                                    </Box>

                                    {/* Data entry form */}
                                    <GRRDataEntryTable
                                        ref={dataEntryRef}
                                        onStudyComplete={handleStudyComplete}
                                        onError={() => setSubmitting(false)}
                                    />
                                </Box>
                            </Drawer>
                        )}

                        {/* ---------------------------------------------------------- */}
                        {/* Main content area                                           */}
                        {/* ---------------------------------------------------------- */}
                        <Box
                            component="main"
                            sx={{
                                flex: 1,
                                overflow: 'auto',
                                p: { xs: 2, md: 3 },
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 3,
                            }}
                        >
                            {/* Floating re-open button when drawer is collapsed */}
                            {isType2 && !drawerOpen && (
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => setDrawerOpen(true)}
                                    startIcon={<ScienceIcon />}
                                    sx={{
                                        alignSelf: 'flex-start',
                                        borderColor: 'rgba(144,202,249,0.4)',
                                        color: '#90caf9',
                                        '&:hover': { borderColor: '#90caf9', backgroundColor: 'rgba(144,202,249,0.06)' },
                                    }}
                                >
                                    Open Data Entry
                                </Button>
                            )}

                            {/* ---- Study Type Selector ---- */}
                            {!studyType && (
                                <MSAStudyTypeSelector onSelect={handleSelectStudyType} />
                            )}

                            {/* ---- GRR Results ---- */}
                            {isType2 && hasResults && (
                                <>
                                    <Paper sx={{ p: { xs: 2, md: 2.5 }, backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 2 }}>
                                            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                                                {studyResults.description}
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>
                                                {studyResults.createdAt ? new Date(studyResults.createdAt).toLocaleString() : ''}
                                            </Typography>
                                        </Box>
                                        <GRRResultsSection results={studyResults.results} />
                                    </Paper>

                                    <GRRChartSection
                                        results={studyResults.results}
                                        measurements={studyResults.measurements}
                                    />
                                </>
                            )}

                            {/* ---- Empty state for type2 (no results yet) ---- */}
                            {isType2 && !hasResults && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, gap: 2, opacity: 0.5, pt: 8 }}>
                                    <ScienceIcon sx={{ fontSize: 56, color: 'rgba(144,202,249,0.4)' }} />
                                    <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                                        Enter study data and click <strong>Run Study</strong> to see results.
                                    </Typography>
                                </Box>
                            )}

                            {/* ---- Previous Studies ---- */}
                            <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />

                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                    <TableChartIcon sx={{ fontSize: 18, color: 'rgba(255,255,255,0.4)' }} />
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'rgba(255,255,255,0.7)' }}>
                                        Previous Studies
                                    </Typography>
                                    {historyLoading && <CircularProgress size={14} sx={{ color: 'rgba(255,255,255,0.3)' }} />}
                                </Box>

                                <Paper sx={{ backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', overflow: 'hidden' }}>
                                    <StudyHistoryTable
                                        studies={history}
                                        onSelect={null}   // future: reload a past study
                                    />
                                </Paper>
                            </Box>
                        </Box>
                    </>
                )}
            </Box>
        </Box>
    );
}

export default MSAStudyPage;
